import { Check, CreditCard } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PaymentHistory } from './PaymentHistory';

import type { User } from '@common/schemas/user.schema';

interface FreeSubscriptionViewProps {
  dbUser: User;
  benefitsList: string[];
  handleManageBilling: () => Promise<void>;
}

export function FreeSubscriptionView({
  dbUser,
  benefitsList,
  handleManageBilling,
}: FreeSubscriptionViewProps) {
  const { t } = useTranslation();

  return (
    <>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          {t('profile.title')}
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Alert severity="info" sx={{ mb: 3 }}>
          {t('profile.viewPremium')}
        </Alert>

        <Box sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('profile.currentPlan')}
          </Typography>
          <Typography variant="h6" fontWeight={600}>
            {t('pricing.plans.free.name')}
          </Typography>
        </Box>

        {/* Features */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom fontWeight={600}>
            {t('profile.includedFeatures')}
          </Typography>
          {benefitsList.length > 0 ? (
            <List dense>
              {benefitsList.map((feature: string, idx: number) => (
                <ListItem key={idx} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Check sx={{ fontSize: 18, color: 'success.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('common.none')}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mt: 2 }}>
          <Button
            component={Link}
            to="/pricing"
            variant="contained"
            size="large"
            fullWidth
          >
            {t('profile.viewPremium')}
          </Button>
          {dbUser.stripeCustomerId && (
            <Button
              variant="contained"
              onClick={handleManageBilling}
              fullWidth
              startIcon={<CreditCard />}
              color="warning"
            >
              {t('profile.manageBilling')}
            </Button>
          )}
        </Box>
      </Paper>
      {dbUser.stripeCustomerId && <PaymentHistory />}
    </>
  );
}
